import { Button } from '../components/Button';
import { useDefinedParams } from '../hooks/useDefinedParams';
import { getContractsSuccessType, getContractsErrorType } from '../../../APIDocs/apiTypes';
import { useJbpQuery, RestTypes } from '../hooks/useJbpQuery';
import { ContractsLoading } from './loadingElements/ContractsLoading';
import { ExternalLink } from '../components/ExternalLink';
import { Typography } from '../components/Typography';
import { EnvironmentVariables } from '../EnvironmentVariables';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { FormButton } from '@portal_components/components/FormButton';
import { useAppDispatch } from '../store';
import { setContract } from '../reducers/jbpGeneralSlice';
import { Title } from '@portal_components/components/Title';

type ContractFormValues = {
  contractId: string;
};

export const Contract = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const portfolioLink = EnvironmentVariables.PortfolioLink;
  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);

  const onContractsLoaded = (contracts: getContractsSuccessType) => {
    console.log(`Contracts loaded: ${JSON.stringify(contracts)}`);
    if (contracts.length === 1) {
      const contract = contracts[0];
      dispatch(
        setContract({
          contractId: contract.ContractId,
          contractCustomerName: contract.CustomerName,
          contractDisplayName: getContractDisplayName(contract)
        })
      );
      navigate(`/site/${mpan}/${serviceLine}`);
    }
  };

  const { data, isLoading: contractsLoading } = useJbpQuery<getContractsSuccessType, getContractsErrorType>(
    RestTypes.Get,
    undefined,
    [`contract-options-${mpan}-${serviceLine}`],
    `jbp/${mpan}/contract-options`,
    true,
    serviceLine,
    onContractsLoaded,
    undefined
  );

  const routeToSearchScreen = () => {
    navigate('/');
  };

  const getContractDisplayName = (contract: getContractsSuccessType[number] | undefined): string => {
    if (contract === undefined) return '';
    const displaySiteEffectiveFrom = contract.EffectiveDate != null ? `, Site Effective From: ${contract.EffectiveDate}` : '';
    return `${contract.CustomerName} (Contract ID: ${contract.ContractId}${displaySiteEffectiveFrom})`;
  };

  const handleSubmit = (formData: ContractFormValues) => {
    if (!data) return;
    const contract = data.find(contract => contract.ContractId === formData.contractId);
    if (!contract) return;
    dispatchSetContractAndNavigate(contract);
  };

  const dispatchSetContractAndNavigate = (contract: getContractsSuccessType[number]) => {
    // this would be way better using redux
    dispatch(
      setContract({
        contractId: contract.ContractId,
        contractCustomerName: contract.CustomerName,
        contractDisplayName: getContractDisplayName(contract)
      })
    );
    navigate(`/site/${mpan}/${serviceLine}`);
  };

  const methods = useForm<ContractFormValues>({});
  const selectedContractId = methods.watch('contractId');

  if (data?.length === 1) return null;

  return (
    <div id="jbp-contracts-screen">
      <div className="w-1/2 inline-block align-top">
        <div className="ml-10">
          <Title className="mt-7 mb-0" text={`MPAN: ${mpan}`} />
          {contractsLoading ? (
            <div>
              <ContractsLoading className="mt-2 w-1/3" />
            </div>
          ) : !data || data?.length === 0 ? (
            <div>
              <Typography
                className="inline-block mr-1 pt-5"
                content={`No ${serviceLine} Contracts found for this MPAN. If you wish to associate a contract to this Site, please use the `}
              ></Typography>
              <ExternalLink className="inline-block" link={portfolioLink} linkText="Stark Portfolio Portal" openInNewTab={true} />
              <br></br>
              <Button text="Back to Search Screen" primary={true} onClick={routeToSearchScreen} className="mt-2" widthStyling="w-2/8" disabled={false} />
            </div>
          ) : (
            <div className="max-w-[30em]">
              <Title className="mt-7 mb-4" text="Contract ID:" />
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                  <FormSearchDropdown<ContractFormValues>
                    name="contractId"
                    title="Select Contract"
                    elementsForDisplay={data?.map(contract => ({
                      title: getContractDisplayName(contract),
                      value: contract.ContractId
                    }))}
                    placeholder="Select Contract..."
                  />
                  <FormButton type="submit" primary icon disabled={!selectedContractId}>
                    Continue
                  </FormButton>
                </form>
              </FormProvider>
            </div>
          )}
        </div>
      </div>
      <div className="inline-block w-1/2 h-screen bg-center bg-origin-content bg-cover" style={{ backgroundImage: 'url(/searchImagery.jpeg)' }}></div>
    </div>
  );
};
